import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Box, Package, Check, Download, Trash2, RefreshCw, Plus, Minus, ScanLine, Volume2, AlertTriangle, MapPin, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const API = process.env.REACT_APP_BACKEND_URL;

// Sound effects for scan feedback
const playSuccessSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    oscillator.frequency.value = 800;
    oscillator.type = 'sine';
    gainNode.gain.value = 0.3;
    oscillator.start();
    setTimeout(() => oscillator.stop(), 150);
  } catch (e) {
    console.log('Audio not supported');
  }
};

const playErrorSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    oscillator.frequency.value = 300;
    oscillator.type = 'square';
    gainNode.gain.value = 0.3;
    oscillator.start();
    setTimeout(() => oscillator.stop(), 300);
  } catch (e) {
    console.log('Audio not supported');
  }
};

const PackingWorkstation = () => {
  const navigate = useNavigate();
  const params = useParams();
  // Slip numbers can contain slashes (e.g. "C/16772/368"), so this route uses
  // a splat ("/wms/fg/packing/*") and the full slip number is in params['*'].
  const slipNumber = params['*'] || params.slipNumber;
  const [slipInfo, setSlipInfo] = useState(null);
  const [items, setItems] = useState([]);
  const [boxes, setBoxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [packSelections, setPackSelections] = useState({});
  // Seasonal: pre-printed small-box sticker capture at Create Box time
  const [showBoxNoDialog, setShowBoxNoDialog] = useState(false);
  const [smallBoxNo, setSmallBoxNo] = useState('');
  
  // USN Scanner state
  const [scannerMode, setScannerMode] = useState(false);
  const [scanInput, setScanInput] = useState('');
  const [scanning, setScanning] = useState(false);
  const [lastScannedItem, setLastScannedItem] = useState(null);
  const scanInputRef = useRef(null);

  // 2026-05-26 — Bulk client-side scan queue (Salem packing latency fix).
  // Pre-loaded eligible USN whitelist (map<usn-upper, item meta>) lets
  // every scan validate without an HTTP round-trip. The actual server
  // persistence happens once at "Create Box" time via /scan-usn/bulk.
  // Restored from localStorage on mount so a tab refresh doesn't lose
  // pending scans.
  const eligibleMapRef = useRef(new Map());           // usn → {line_index, barcode, ...}
  const alreadyScannedRef = useRef(new Set());        // server-known + this-session in-flight
  const [eligibleReady, setEligibleReady] = useState(false);
  const [scanQueue, setScanQueue] = useState([]);     // [{usn, line_index, barcode, ts}]
  const [flushing, setFlushing] = useState(false);
  // Persist queue per-slip so refresh / device sleep doesn't lose scans.
  const queueKey = `packing_scan_queue::${slipNumber}`;
  
  // Extra pieces state
  const [extraPieces, setExtraPieces] = useState([]);
  const [showExtraPiecesDialog, setShowExtraPiecesDialog] = useState(false);
  const [confirmingPutaway, setConfirmingPutaway] = useState(false);
  // Close-carton error gate: surface scan errors (extra / not-picked USNs)
  // and make the operator acknowledge them before sealing the box.
  const [showCloseErrorDialog, setShowCloseErrorDialog] = useState(false);
  const [closeErrorAck, setCloseErrorAck] = useState(false);
  
  // Packer group selection state (box credit is split equally across members)
  const [packerGroups, setPackerGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  
  // Auto-focus scanner when in scanner mode
  useEffect(() => {
    if (scannerMode && scanInputRef.current) {
      scanInputRef.current.focus();
    }
  }, [scannerMode, lastScannedItem]);

  const fetchSlipData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('session_token');
      
      // Fetch items for packing
      const itemsRes = await fetch(`${API}/api/wms/packing/slip/${slipNumber}/items`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Parse JSON once
      const itemsData = await itemsRes.json().catch(() => null);
      
      // Check response status after parsing
      if (!itemsRes.ok) {
        throw new Error(itemsData?.detail || 'Failed to fetch slip items');
      }
      
      if (!itemsData) {
        throw new Error('Invalid response from server');
      }
      
      setSlipInfo({
        pick_slip_number: itemsData.pick_slip_number,
        customer: itemsData.customer,
        agent: itemsData.agent,
        dc_number: itemsData.dc_number,
        transport_name: itemsData.transport_name,
        is_seasonal: itemsData.is_seasonal
      });
      
      // Check if all items are packed
      const packingItems = itemsData.items || [];
      setItems(packingItems);
      
      // Initialize pack selections - use line_index as key to handle duplicate barcodes
      const initialSelections = {};
      packingItems.forEach((item, idx) => {
        // Use line_index from backend or fallback to array index
        const key = item.line_index !== undefined ? item.line_index : idx;
        initialSelections[key] = {
          selected: false,
          qty: item.remaining_qty,
          barcode: item.barcode,
          order_no: item.order_no || '',
          line_index: item.line_index !== undefined ? item.line_index : idx
        };
      });
      setPackSelections(initialSelections);
      
      // Fetch existing boxes
      const boxesRes = await fetch(`${API}/api/wms/packing/boxes/${slipNumber}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (boxesRes.ok) {
        const boxesData = await boxesRes.json();
        setBoxes(boxesData.boxes || []);
      }
      
      // If no items left to pack, show success message
      if (packingItems.length === 0) {
        toast.success('All items have been packed for this slip!');
      }
      
      // Fetch extra pieces for this slip
      await fetchExtraPieces();
    } catch (error) {
      console.error('Error fetching slip data:', error);
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [slipNumber]);
  
  const fetchExtraPieces = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/api/wms/packing/extra-pieces/session?pick_slip_number=${slipNumber}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setExtraPieces(data.extra_pieces || []);
      }
    } catch (error) {
      console.error('Error fetching extra pieces:', error);
    }
  };
  
  const fetchPackerGroups = useCallback(async () => {
    try {
      const response = await apiClient.get('/wms/dispatch/packer-groups');
      setPackerGroups(response.data?.groups || []);
    } catch (error) {
      console.error('Error fetching packer groups:', error);
    }
  }, []);
  
  const handleConfirmPutaway = async () => {
    if (extraPieces.length === 0) return;
    
    setConfirmingPutaway(true);
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/api/wms/packing/extra-pieces/confirm-putaway`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          usn_codes: extraPieces.map(p => p.usn_code)
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setExtraPieces([]);
        setShowExtraPiecesDialog(false);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to confirm put away');
      }
    } catch (error) {
      toast.error('Failed to confirm put away');
    } finally {
      setConfirmingPutaway(false);
    }
  };

  useEffect(() => {
    fetchSlipData();
    fetchPackerGroups();
  }, [fetchSlipData, fetchPackerGroups]);

  // 2026-05-26 — Pre-load the eligible USN whitelist + restore queue
  // from localStorage when the slip changes. Keeps every subsequent
  // scan a pure in-memory operation.
  useEffect(() => {
    if (!slipNumber) return;
    let cancelled = false;
    setEligibleReady(false);
    (async () => {
      try {
        const res = await apiClient.get(`/wms/packing/eligible-usns/${encodeURIComponent(slipNumber)}`);
        if (cancelled) return;
        const map = new Map();
        for (const [usn, meta] of Object.entries(res.data?.eligible || {})) {
          map.set(String(usn).toUpperCase(), meta);
        }
        eligibleMapRef.current = map;
        const scanned = new Set();
        for (const code of Object.keys(res.data?.already_scanned || {})) {
          scanned.add(String(code).toUpperCase());
        }
        for (const code of (res.data?.already_packed_legacy || [])) {
          scanned.add(String(code).toUpperCase());
        }
        alreadyScannedRef.current = scanned;
        setEligibleReady(true);

        // Restore queue (if any) — keep only USNs still eligible and
        // not already-scanned per the server.
        try {
          const stash = localStorage.getItem(queueKey);
          if (stash) {
            const persisted = JSON.parse(stash);
            const survivors = (persisted || []).filter((q) => {
              const code = String(q.usn || '').toUpperCase();
              return map.has(code) && !scanned.has(code);
            });
            if (survivors.length) {
              setScanQueue(survivors);
              // Pre-increment qty so the line counters reflect the queue.
              setPackSelections((prev) => {
                const next = { ...prev };
                for (const q of survivors) {
                  const lineKey = q.line_index;
                  const existing = next[lineKey] || {};
                  next[lineKey] = {
                    ...existing,
                    selected: true,
                    qty: (existing.qty || 0) + 1,
                    barcode: q.barcode,
                    order_no: existing.order_no || '',
                    line_index: lineKey,
                  };
                }
                return next;
              });
              // Mark them in the in-flight ref so duplicate guard works.
              for (const q of survivors) {
                alreadyScannedRef.current.add(String(q.usn).toUpperCase());
              }
              toast.info(`Restored ${survivors.length} queued scan(s) from local cache`);
            } else {
              localStorage.removeItem(queueKey);
            }
          }
        } catch { /* swallow JSON parse errors */ }
      } catch (e) {
        // Slip might be 404 or transient — fall back to the legacy
        // per-scan endpoint (handleScanUSN still works that path).
        console.error('eligible-usns load failed:', e);
        setEligibleReady(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slipNumber]);

  // 2026-05-26 — Persist queue to localStorage on every change.
  useEffect(() => {
    try {
      if (scanQueue.length) {
        localStorage.setItem(queueKey, JSON.stringify(scanQueue));
      } else {
        localStorage.removeItem(queueKey);
      }
    } catch { /* quota — ignore */ }
  }, [scanQueue, queueKey]);

  // 2026-05-26 — Client-side scan handler.
  // Look up the USN in the pre-loaded whitelist; everything is in-memory
  // so the scanner can fire 30+ scans/sec without any HTTP round-trip.
  // Server persistence happens once on Create Box via /scan-usn/bulk.
  const handleScanUSN = async (e) => {
    e.preventDefault();
    const usn = scanInput.trim().toUpperCase();

    if (!usn) {
      if (scanInputRef.current) scanInputRef.current.focus();
      return;
    }
    if (scanning) return;
    setScanning(true);
    setScanInput('');

    try {
      // Fall back to the legacy per-scan endpoint if the whitelist
      // hasn't loaded yet (network race) — keeps the workstation usable.
      if (!eligibleReady) {
        await legacyScanFallback(usn);
        return;
      }

      // 1) Duplicate check (already on server, or already in this queue).
      if (alreadyScannedRef.current.has(usn)) {
        playErrorSound();
        if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
        toast.error(`USN ${usn} already scanned for this slip`);
        return;
      }

      // 2) Whitelist lookup.
      const meta = eligibleMapRef.current.get(usn);
      if (!meta) {
        // EXTRA piece path — surface it inline. The server will resolve
        // the original bin when the queue flushes (or the operator
        // confirms-putaway). For now, just record it client-side so
        // they get instant visual feedback.
        playErrorSound();
        if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
        setExtraPieces((prev) => {
          if (prev.some((p) => p.usn_code === usn)) return prev;
          return [...prev, {
            usn_code: usn,
            original_bin: null,
            sku_code: null,
            detected_at: new Date().toISOString(),
          }];
        });
        toast.error(`EXTRA PIECE! USN ${usn} was not picked for this slip.`, {
          duration: 4500,
        });
        return;
      }

      // 3) Accepted — increment line + push to queue.
      playSuccessSound();
      if (navigator.vibrate) navigator.vibrate(100);

      const lineKey = meta.line_index;
      setPackSelections((prev) => {
        const existing = prev[lineKey] || {};
        return {
          ...prev,
          [lineKey]: {
            ...existing,
            selected: true,
            qty: (existing.qty || 0) + 1,
            barcode: meta.barcode,
            order_no: meta.order_no || existing.order_no || '',
            line_index: lineKey,
          },
        };
      });
      setScanQueue((prev) => [
        ...prev,
        { usn, line_index: lineKey, barcode: meta.barcode, ts: Date.now() },
      ]);
      alreadyScannedRef.current.add(usn);
      setLastScannedItem({
        ...meta,
        usn_code: usn,
      });
      toast.success(
        `✓ ${meta.design_name || meta.barcode}${meta.shade ? ' · ' + meta.shade : ''}${meta.size ? ' · ' + meta.size : ''}`,
        { duration: 1500 },
      );
    } finally {
      setScanning(false);
      if (scanInputRef.current) scanInputRef.current.focus();
    }
  };

  // Legacy single-scan path — kept as a safety fallback if the
  // whitelist preload failed (e.g. network race). Identical behaviour
  // to the pre-2026-05-26 packing workflow.
  const legacyScanFallback = async (usn) => {
    try {
      const response = await apiClient.post('/wms/packing/scan-usn', {
        pick_slip_number: slipNumber,
        usn_code: usn,
      });
      const data = response.data;
      playSuccessSound();
      if (navigator.vibrate) navigator.vibrate(100);
      const itemInfo = data.item;
      const lineKey = itemInfo.line_index;
      setPackSelections((prev) => {
        const existing = prev[lineKey] || {};
        return {
          ...prev,
          [lineKey]: {
            ...existing,
            selected: true,
            qty: (existing.qty || 0) + 1,
            barcode: itemInfo.barcode,
            order_no: itemInfo.order_no || '',
            line_index: lineKey,
          },
        };
      });
      setLastScannedItem({ ...itemInfo, usn_code: data.usn_code });
      toast.success(`✓ ${itemInfo.design_name} - ${itemInfo.shade} - ${itemInfo.size}`);
    } catch (error) {
      playErrorSound();
      if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
      const errorData = error.response?.data;
      if (errorData?.detail) {
        if (typeof errorData.detail === 'object' && errorData.detail.is_extra_piece) {
          setExtraPieces((prev) => [...prev, {
            usn_code: errorData.detail.usn_code,
            original_bin: errorData.detail.original_bin,
            sku_code: errorData.detail.sku_code,
            detected_at: new Date().toISOString(),
          }]);
          toast.error(`EXTRA PIECE! USN ${usn} was not picked for this slip.`, { duration: 5000 });
        } else {
          toast.error(typeof errorData.detail === 'string' ? errorData.detail : 'Wrong USN scanned');
        }
      } else {
        toast.error('Scan error: ' + (error.message || 'Unknown error'));
      }
    }
  };

  // Use lineKey (line_index) for all selection operations to handle duplicate barcodes
  const handleCheckboxChange = (lineKey, checked) => {
    setPackSelections(prev => ({
      ...prev,
      [lineKey]: {
        ...prev[lineKey],
        selected: checked
      }
    }));
  };

  const handleQtyChange = (lineKey, value, maxQty) => {
    const numValue = parseInt(value) || 0;
    const clampedValue = Math.max(0, Math.min(numValue, maxQty));
    
    setPackSelections(prev => ({
      ...prev,
      [lineKey]: {
        ...prev[lineKey],
        qty: clampedValue,
        selected: clampedValue > 0 ? prev[lineKey]?.selected : false
      }
    }));
  };

  const incrementQty = (lineKey, maxQty) => {
    setPackSelections(prev => ({
      ...prev,
      [lineKey]: {
        ...prev[lineKey],
        qty: Math.min((prev[lineKey]?.qty || 0) + 1, maxQty)
      }
    }));
  };

  const decrementQty = (lineKey) => {
    setPackSelections(prev => ({
      ...prev,
      [lineKey]: {
        ...prev[lineKey],
        qty: Math.max((prev[lineKey]?.qty || 0) - 1, 0)
      }
    }));
  };

  const getSelectedItems = () => {
    return Object.entries(packSelections)
      .filter(([_, sel]) => sel.selected && sel.qty > 0)
      .map(([key, sel]) => ({
        barcode: sel.barcode,
        pack_qty: sel.qty,
        line_index: sel.line_index,
        order_no: sel.order_no
      }));
  };

  const getTotalSelectedQty = () => {
    return Object.values(packSelections)
      .filter(sel => sel.selected)
      .reduce((sum, sel) => sum + (sel.qty || 0), 0);
  };

  const handleCreateBox = async () => {
    const selectedItems = getSelectedItems();
    
    if (selectedItems.length === 0) {
      toast.error('Please select at least one item to pack');
      return;
    }
    
    if (!selectedGroup) {
      toast.error('Please select a packer group before creating a box');
      return;
    }

    // Close-carton error gate — if any scanned USN errored (extra / not
    // picked for this slip), DON'T silently seal the box and leave those
    // pieces out. List them and make the operator acknowledge first.
    if (extraPieces.length > 0) {
      setCloseErrorAck(false);
      setShowCloseErrorDialog(true);
      return;
    }

    proceedToCreateBox();
  };

  // Runs the actual carton creation (seasonal first captures the small-box no).
  const proceedToCreateBox = () => {
    if (slipInfo?.is_seasonal) {
      setSmallBoxNo('');
      setShowBoxNoDialog(true);
      return;
    }
    doCreateBox(null);
  };

  // Operator acknowledged the errored pieces are set aside → proceed to close.
  const confirmCloseWithErrors = () => {
    setShowCloseErrorDialog(false);
    proceedToCreateBox();
  };

  const doCreateBox = async (externalBoxNo) => {
    const selectedItems = getSelectedItems();
    
    if (selectedItems.length === 0) {
      toast.error('Please select at least one item to pack');
      return;
    }
    
    if (!selectedGroup) {
      toast.error('Please select a packer group before creating a box');
      return;
    }
    
    try {
      setCreating(true);

      // 2026-05-26 — Flush the client-side scan queue FIRST (atomic
      // bulk persist of every USN scanned for this carton). Only when
      // the queue persists cleanly do we proceed with the carton
      // creation — so a server-rejected USN doesn't leak into the box.
      if (scanQueue.length > 0) {
        setFlushing(true);
        try {
          const flush = await apiClient.post('/wms/packing/scan-usn/bulk', {
            pick_slip_number: slipNumber,
            usn_codes: scanQueue.map((q) => q.usn),
          });
          const { rejected = [], summary = {} } = flush.data || {};
          if (rejected.length) {
            // Surface unexpected rejections — the carton creation aborts
            // so the operator can resolve them before sealing the box.
            const head = rejected.slice(0, 3).map((r) => `${r.usn_code}: ${r.reason}`).join('\n');
            const more = rejected.length > 3 ? `\n…and ${rejected.length - 3} more` : '';
            toast.error(
              `Cannot create carton — ${rejected.length} queued scan(s) were rejected by the server:\n${head}${more}`,
              { duration: 8000 },
            );
            // Drop the rejected ones from the queue so a retry only
            // re-sends the still-valid ones.
            const badSet = new Set(rejected.map((r) => String(r.usn_code).toUpperCase()));
            setScanQueue((prev) => prev.filter((q) => !badSet.has(String(q.usn).toUpperCase())));
            return;
          }
          toast.success(
            `Flushed ${summary.accepted_count ?? scanQueue.length} scan(s) to server`,
            { duration: 1500 },
          );
        } catch (flushErr) {
          const msg = flushErr.response?.data?.detail || 'Bulk scan flush failed';
          toast.error(msg);
          return;
        } finally {
          setFlushing(false);
        }
      }

      const response = await apiClient.post('/wms/packing/create-box', {
        pick_slip_number: slipNumber,
        items: selectedItems,
        group_id: selectedGroup.group_id,
        external_box_no: externalBoxNo || undefined
      });
      
      const data = response.data;
      toast.success(`Box ${data.box_number} created by group ${selectedGroup.group_name}`);

      setShowBoxNoDialog(false);
      setSmallBoxNo('');

      // Clear queue + persisted cache — carton is sealed.
      setScanQueue([]);
      try { localStorage.removeItem(queueKey); } catch { /* noop */ }
      // Refresh data
      fetchSlipData();
    } catch (error) {
      console.error('Error creating box:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to create box';
      toast.error(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const handleDownloadSlip = async (boxId, boxNumber) => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/api/wms/packing/box/${boxId}/slip-pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to download slip');
      
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `packing_slip_${boxNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Packing slip downloaded');
    } catch (error) {
      console.error('Error downloading slip:', error);
      toast.error('Failed to download packing slip');
    }
  };

  const handleDeleteBox = async (boxId, boxNumber) => {
    if (!window.confirm(`Are you sure you want to delete ${boxNumber}?`)) return;
    
    try {
      await apiClient.delete(`/wms/packing/box/${boxId}`);
      toast.success('Box deleted');
      fetchSlipData();
    } catch (error) {
      console.error('Error deleting box:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to delete box';
      toast.error(errorMessage);
    }
  };

  const selectAll = () => {
    const newSelections = {};
    items.forEach((item, idx) => {
      const lineKey = item.line_index !== undefined ? item.line_index : idx;
      newSelections[lineKey] = {
        selected: true,
        qty: item.remaining_qty,
        barcode: item.barcode,
        order_no: item.order_no || '',
        line_index: lineKey
      };
    });
    setPackSelections(newSelections);
  };

  const clearAll = () => {
    const newSelections = {};
    items.forEach((item, idx) => {
      const lineKey = item.line_index !== undefined ? item.line_index : idx;
      newSelections[lineKey] = {
        selected: false,
        qty: item.remaining_qty,
        barcode: item.barcode,
        order_no: item.order_no || '',
        line_index: lineKey
      };
    });
    setPackSelections(newSelections);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-[#2D5F4F]" />
          <p className="mt-2 text-[#5A5F6E]">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/wms/fg/packing')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
              data-testid="packing-workstation-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold font-display">{slipNumber}</h1>
              <p className="text-sm text-[#E8DCC8]">{slipInfo?.customer}</p>
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm">
            {slipInfo?.dc_number && (
              <div className="text-right">
                <p className="text-xs text-[#E8DCC8]">DC Number</p>
                <p className="font-medium text-blue-300">{slipInfo.dc_number}</p>
              </div>
            )}
            {slipInfo?.transport_name && (
              <div className="text-right">
                <p className="text-xs text-[#E8DCC8]">Transporter</p>
                <p className="font-medium text-purple-300">{slipInfo.transport_name}</p>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {/* Packer Group Selection */}
        <div className="mb-4 flex items-center gap-3 p-3 bg-cyan-50 border border-cyan-200 rounded-lg" data-testid="packer-selection-bar">
          <span className="text-sm font-semibold text-cyan-800 whitespace-nowrap">Group:</span>
          {packerGroups.length > 0 ? (
            <select
              value={selectedGroup?.group_id || ''}
              onChange={(e) => {
                const grp = packerGroups.find(g => g.group_id === e.target.value);
                setSelectedGroup(grp || null);
              }}
              className="flex-1 max-w-xs border border-cyan-300 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
              data-testid="packer-group-select"
            >
              <option value="">-- Select Group --</option>
              {packerGroups.map(g => (
                <option key={g.group_id} value={g.group_id}>
                  {g.group_name} ({(g.members || []).length} members)
                </option>
              ))}
            </select>
          ) : (
            <span className="text-sm text-red-600">No packer groups created. Please create a group in Dispatch Supervisor → Packers.</span>
          )}
          {selectedGroup && (
            <div className="flex items-center gap-1 flex-wrap">
              <Badge className="bg-cyan-600 text-white">{selectedGroup.group_name}</Badge>
              {(selectedGroup.members || []).map(m => (
                <Badge key={m.staff_id} variant="outline" className="bg-white text-xs">{m.staff_name || m.staff_id}</Badge>
              ))}
            </div>
          )}
        </div>

        {/* USN Scanner Mode Card */}
        <Card className={`mb-6 border-2 ${scannerMode ? 'border-green-500 bg-green-50' : 'border-[#E5DFD0]'}`}>
          <CardHeader className="py-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <ScanLine className={`w-5 h-5 ${scannerMode ? 'text-green-600' : 'text-gray-500'}`} />
                Quick Scan Mode
                {scannerMode && <Badge className="bg-green-600">ACTIVE</Badge>}
                {/* 2026-05-26 — Queued-scans pill. Visible whenever the
                    operator has pending USNs that will flush on Create Box. */}
                {scanQueue.length > 0 && (
                  <Badge
                    className="bg-amber-500 text-white border-amber-600"
                    data-testid="packing-queue-badge"
                    title="Pending USNs — will sync to server on Create Box"
                  >
                    Queued: {scanQueue.length}
                  </Badge>
                )}
                {flushing && (
                  <Badge className="bg-sky-600 text-white">
                    <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                    Flushing…
                  </Badge>
                )}
                {!eligibleReady && scannerMode && (
                  <Badge
                    className="bg-stone-400 text-white"
                    title="Whitelist loading — using legacy per-scan path"
                  >
                    Loading whitelist…
                  </Badge>
                )}
              </CardTitle>
              <Button
                onClick={() => {
                  const newScannerMode = !scannerMode;
                  setScannerMode(newScannerMode);
                  setLastScannedItem(null);
                  
                  // When ENTERING scanner mode, reset all pack quantities to 0
                  if (newScannerMode) {
                    setPackSelections({});  // Clear all selections - quantities start from 0
                    // 2026-05-26 — Also clear any in-flight queue and
                    // in-memory dedupe set so a fresh carton starts
                    // from a clean slate.
                    setScanQueue([]);
                    try { localStorage.removeItem(queueKey); } catch { /* noop */ }
                    // Rebuild the dedupe set from server-known scans only.
                    const fresh = new Set();
                    for (const code of (alreadyScannedRef.current || [])) {
                      // keep server-known scans (they are still duplicates).
                      // We can't tell them apart from in-flight here so we
                      // reset and let the next eligible-usns refresh hydrate.
                      fresh.add(code);
                    }
                    // Conservatively reset — the legacy fallback path
                    // still de-dupes via the server, so no data loss.
                    alreadyScannedRef.current = fresh;
                    toast.info('Scanner mode enabled. All quantities reset to 0.');
                  }
                }}
                variant={scannerMode ? 'default' : 'outline'}
                className={scannerMode ? 'bg-green-600 hover:bg-green-700' : ''}
                data-testid="toggle-scanner-btn"
              >
                <Volume2 className="w-4 h-4 mr-2" />
                {scannerMode ? 'Exit Scanner' : 'Enable Scanner'}
              </Button>
            </div>
          </CardHeader>
          {scannerMode && (
            <CardContent className="pb-4">
              <form onSubmit={handleScanUSN} className="flex gap-3">
                <Input
                  ref={scanInputRef}
                  value={scanInput}
                  onChange={(e) => setScanInput(e.target.value)}
                  placeholder="Scan USN barcode..."
                  className="text-lg font-mono flex-1"
                  autoFocus
                  disabled={scanning}
                  data-testid="usn-scan-input"
                />
                <Button 
                  type="submit" 
                  disabled={scanning || !scanInput.trim()}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {scanning ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Scan'}
                </Button>
              </form>
              
              {/* Last scanned item info */}
              {lastScannedItem && (
                <div className="mt-3 p-3 bg-green-100 rounded-lg border border-green-300">
                  <p className="text-sm text-green-800 font-medium">Last Scanned:</p>
                  <p className="text-green-900 font-bold">
                    {lastScannedItem.design_name} - {lastScannedItem.shade} - {lastScannedItem.size}
                  </p>
                  <p className="text-xs text-green-700">USN: {lastScannedItem.usn_code}</p>
                </div>
              )}
              
              <p className="text-xs text-gray-500 mt-2">
                Scan USN barcodes to auto-select items for packing. Sound feedback enabled.
              </p>
            </CardContent>
          )}
        </Card>

        {/* Extra Pieces Alert Tile */}
        {extraPieces.length > 0 && (
          <Card className="mb-6 border-2 border-orange-400 bg-orange-50">
            <CardContent className="py-4 px-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-200 rounded-full">
                    <AlertTriangle className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-bold text-orange-800 text-lg">
                      {extraPieces.length} Extra Piece{extraPieces.length > 1 ? 's' : ''} Detected
                    </p>
                    <p className="text-sm text-orange-600">
                      These USNs were not picked for this slip. Keep aside for re-put-away.
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowExtraPiecesDialog(true)}
                    className="border-orange-400 text-orange-700 hover:bg-orange-100"
                    data-testid="view-extra-pieces-btn"
                  >
                    View Details
                  </Button>
                  <Button
                    onClick={handleConfirmPutaway}
                    disabled={confirmingPutaway}
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                    data-testid="confirm-putaway-btn"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    {confirmingPutaway ? 'Processing...' : 'Confirm Put Away'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {items.length > 0 && (
          <div
            className="bg-white rounded-lg border border-[#E5DFD0] px-4 py-3 mb-6 flex items-center justify-between sticky top-20 z-10 shadow-sm"
            data-testid="packing-selection-bar"
          >
            <div className="text-sm">
              <span className="text-[#5A5F6E]">Selected: </span>
              <span className="font-semibold text-[#1A1F2E]">
                {getSelectedItems().length} items, {getTotalSelectedQty()} pcs
              </span>
            </div>
            <Button
              onClick={handleCreateBox}
              disabled={creating || getSelectedItems().length === 0}
              className="bg-[#2D5F4F] hover:bg-[#234a3d]"
              data-testid="packing-create-box-btn"
            >
              {creating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Box className="w-4 h-4 mr-2" />
                  Create Box
                </>
              )}
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Items to Pack */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg border border-[#E5DFD0] overflow-hidden">
              <div className="px-4 py-3 bg-[#F8F4EE] border-b border-[#E5DFD0] flex items-center justify-between">
                <h2 className="font-semibold text-[#1A1F2E]">
                  Items to Pack ({items.length})
                </h2>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={selectAll}
                    data-testid="packing-select-all-btn"
                  >
                    Select All
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={clearAll}
                    data-testid="packing-clear-all-btn"
                  >
                    Clear
                  </Button>
                </div>
              </div>
              
              {items.length === 0 ? (
                <div className="p-8 text-center">
                  <Check className="w-12 h-12 mx-auto text-green-500" />
                  <p className="mt-2 text-[#5A5F6E]">All items have been packed!</p>
                  <Button 
                    className="mt-4"
                    onClick={() => navigate('/wms/fg/packing')}
                  >
                    Back to Dashboard
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F8F4EE] text-[#5A5F6E]">
                      <tr>
                        <th className="px-3 py-2 text-left w-10"></th>
                        <th className="px-3 py-2 text-left">Design Name</th>
                        <th className="px-3 py-2 text-left">Shade</th>
                        <th className="px-3 py-2 text-left">Size</th>
                        <th className="px-3 py-2 text-center">Remaining</th>
                        <th className="px-3 py-2 text-center">Pack Qty</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E5DFD0]">
                      {items.map((item, idx) => {
                        // Use line_index from backend or fallback to array index
                        const lineKey = item.line_index !== undefined ? item.line_index : idx;
                        const selection = packSelections[lineKey] || { selected: false, qty: 0 };
                        
                        return (
                          <tr 
                            key={`${item.barcode}_${lineKey}`} 
                            className={`hover:bg-[#FFFBF5] ${selection.selected ? 'bg-green-50' : ''}`}
                          >
                            <td className="px-3 py-3">
                              <Checkbox
                                checked={selection.selected}
                                onCheckedChange={(checked) => handleCheckboxChange(lineKey, checked)}
                                data-testid={`packing-checkbox-${lineKey}`}
                              />
                            </td>
                            <td className="px-3 py-3">
                              <div className="font-medium text-[#1A1F2E]">{item.design_name}</div>
                              <div className="text-xs text-[#5A5F6E]">{item.barcode}</div>
                              {item.order_no && (
                                <div className="text-xs text-blue-600">Order: {item.order_no}</div>
                              )}
                            </td>
                            <td className="px-3 py-3 text-[#5A5F6E]">{item.shade}</td>
                            <td className="px-3 py-3 text-[#5A5F6E]">{item.size}</td>
                            <td className="px-3 py-3 text-center font-medium text-[#2D5F4F]">
                              {item.remaining_qty}
                            </td>
                            <td className="px-3 py-3">
                              <div className="flex items-center justify-center gap-1">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => decrementQty(lineKey)}
                                  data-testid={`packing-decrement-${lineKey}`}
                                >
                                  <Minus className="w-3 h-3" />
                                </Button>
                                <Input
                                  type="number"
                                  value={selection.qty || 0}
                                  onChange={(e) => handleQtyChange(lineKey, e.target.value, item.remaining_qty)}
                                  className="w-16 text-center h-8"
                                  min={0}
                                  max={item.remaining_qty}
                                  data-testid={`packing-qty-${lineKey}`}
                                />
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => incrementQty(lineKey, item.remaining_qty)}
                                  data-testid={`packing-increment-${lineKey}`}
                                >
                                  <Plus className="w-3 h-3" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              
              {items.length > 0 && null}
            </div>
          </div>

          {/* Created Boxes */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg border border-[#E5DFD0] overflow-hidden sticky top-24">
              <div className="px-4 py-3 bg-[#F8F4EE] border-b border-[#E5DFD0]">
                <h2 className="font-semibold text-[#1A1F2E]">
                  Created Boxes ({boxes.length})
                </h2>
              </div>
              
              {boxes.length === 0 ? (
                <div className="p-6 text-center">
                  <Package className="w-10 h-10 mx-auto text-gray-300" />
                  <p className="mt-2 text-sm text-[#5A5F6E]">No boxes created yet</p>
                </div>
              ) : (
                <div className="divide-y divide-[#E5DFD0] max-h-[60vh] overflow-y-auto">
                  {boxes.map((box) => (
                    <div key={box.box_id} className="p-4" data-testid={`packing-box-${box.box_number}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="font-semibold text-[#1A1F2E]">{box.box_number}</div>
                          {box.external_box_no && (
                            <div className="text-xs font-mono text-amber-700" data-testid={`packing-box-sticker-${box.box_number}`}>
                              Sticker: {box.external_box_no}
                            </div>
                          )}
                          <div className="text-xs text-[#5A5F6E]">
                            {box.items?.length} items, {box.total_qty} pcs
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleDownloadSlip(box.box_id, box.box_number)}
                            title="Download Packing Slip"
                            data-testid={`packing-download-${box.box_number}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 text-red-500 hover:text-red-700"
                            onClick={() => handleDeleteBox(box.box_id, box.box_number)}
                            title="Delete Box"
                            data-testid={`packing-delete-${box.box_number}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="text-xs text-[#5A5F6E] space-y-1">
                        {box.items?.slice(0, 3).map((item, idx) => (
                          <div key={idx} className="truncate">
                            {item.design_name} - {item.qty} pcs
                          </div>
                        ))}
                        {box.items?.length > 3 && (
                          <div className="text-[#2D5F4F]">
                            +{box.items.length - 3} more items
                          </div>
                        )}
                      </div>
                      <div className="mt-2 text-xs text-[#5A5F6E]">
                        Packed by: {box.packer_name}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      
      {/* Extra Pieces Dialog */}
      <Dialog open={showExtraPiecesDialog} onOpenChange={setShowExtraPiecesDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-700">
              <AlertTriangle className="w-5 h-5" />
              Extra Pieces for Re-Put-Away
            </DialogTitle>
          </DialogHeader>
          
          <div className="mt-4">
            <p className="text-sm text-gray-600 mb-4">
              These USNs were scanned but were NOT picked for this slip. They need to be put back in their original bins.
            </p>
            
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-orange-100">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-orange-800">#</th>
                    <th className="px-4 py-2 text-left font-medium text-orange-800">USN Code</th>
                    <th className="px-4 py-2 text-left font-medium text-orange-800">Original Bin</th>
                    <th className="px-4 py-2 text-left font-medium text-orange-800">SKU</th>
                  </tr>
                </thead>
                <tbody>
                  {extraPieces.map((piece, idx) => (
                    <tr key={piece.usn_code} className={idx % 2 === 0 ? 'bg-white' : 'bg-orange-50'}>
                      <td className="px-4 py-2 text-gray-600">{idx + 1}</td>
                      <td className="px-4 py-2 font-mono font-medium">{piece.usn_code}</td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-1">
                          <MapPin className="w-4 h-4 text-blue-500" />
                          <span className="font-medium text-blue-700">{piece.original_bin || 'Unknown'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-gray-600">{piece.sku_code || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Instructions:</strong> Keep these pieces aside and put them back in their original bins. 
                Click "Confirm Put Away" when done to update inventory.
              </p>
            </div>
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowExtraPiecesDialog(false)}>
              Close
            </Button>
            <Button 
              onClick={handleConfirmPutaway}
              disabled={confirmingPutaway || extraPieces.length === 0}
              className="bg-orange-600 hover:bg-orange-700"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              {confirmingPutaway ? 'Processing...' : `Confirm Put Away (${extraPieces.length} pcs)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close-carton error gate — list errored USNs, require acknowledgement */}
      <Dialog open={showCloseErrorDialog} onOpenChange={setShowCloseErrorDialog}>
        <DialogContent className="sm:max-w-lg" data-testid="close-carton-error-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-700">
              <AlertTriangle className="w-5 h-5" />
              Resolve scan errors before closing
            </DialogTitle>
            <DialogDescription>
              {extraPieces.length} piece{extraPieces.length > 1 ? 's' : ''} you scanned
              {extraPieces.length > 1 ? ' were' : ' was'} <strong>not picked for this slip</strong> and
              will <strong>NOT</strong> be packed into this carton. Set
              {extraPieces.length > 1 ? ' them' : ' it'} aside for put-away to the original bin,
              then confirm to close the carton with the valid pieces only.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-56 overflow-auto rounded-md border border-orange-200 bg-orange-50 divide-y divide-orange-200" data-testid="close-carton-error-list">
            {extraPieces.map((p, idx) => (
              <div key={`${p.usn_code}-${idx}`} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`close-carton-error-usn-${p.usn_code}`}>
                <div>
                  <span className="font-semibold text-orange-800">{p.usn_code}</span>
                  {p.sku_code ? <span className="text-orange-600"> · {p.sku_code}</span> : null}
                </div>
                <span className="text-xs text-orange-600">
                  {p.original_bin ? `Return to ${p.original_bin}` : 'Not picked for this slip'}
                </span>
              </div>
            ))}
          </div>

          <label className="flex items-start gap-2 mt-3 cursor-pointer select-none" data-testid="close-carton-ack-label">
            <Checkbox
              checked={closeErrorAck}
              onCheckedChange={(c) => setCloseErrorAck(!!c)}
              className="mt-0.5"
              data-testid="close-carton-ack-checkbox"
            />
            <span className="text-sm text-[#1A1F2E]">
              I have set {extraPieces.length > 1 ? 'these pieces' : 'this piece'} aside for put-away.
              Close the carton with the {getTotalSelectedQty()} valid piece{getTotalSelectedQty() > 1 ? 's' : ''}.
            </span>
          </label>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCloseErrorDialog(false)} data-testid="close-carton-error-cancel">
              Go back &amp; re-scan
            </Button>
            <Button
              onClick={confirmCloseWithErrors}
              disabled={!closeErrorAck}
              className="bg-[#2D5F4F] hover:bg-[#234a3d]"
              data-testid="close-carton-error-confirm"
            >
              Confirm &amp; close carton
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Seasonal: capture the pre-printed small-box sticker number */}
      <Dialog open={showBoxNoDialog} onOpenChange={(o) => { if (!creating) setShowBoxNoDialog(o); }}>
        <DialogContent className="sm:max-w-md" data-testid="small-box-no-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Box className="w-5 h-5 text-amber-600" /> Small Box Number
            </DialogTitle>
            <DialogDescription>
              Scan or type the pre-printed sticker number for this small box, then create it.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 space-y-3">
            <Input
              autoFocus
              value={smallBoxNo}
              onChange={(e) => setSmallBoxNo(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && smallBoxNo.trim() && !creating) doCreateBox(smallBoxNo.trim()); }}
              placeholder="Scan small box sticker…"
              className="font-mono"
              data-testid="small-box-no-input"
            />
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowBoxNoDialog(false)} disabled={creating} data-testid="small-box-no-cancel">
              Cancel
            </Button>
            <Button
              onClick={() => doCreateBox(smallBoxNo.trim())}
              disabled={creating || !smallBoxNo.trim()}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="small-box-no-confirm"
            >
              {creating ? 'Creating…' : 'Create Box'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PackingWorkstation;
